import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Trophy, Gauge, Clock, DollarSign, Shield, TrendingUp,
  Loader2, CheckCircle2, ArrowRight, Package, PlusCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P6 — Proposal Comparison page.
 * URL: /admin/procurement/rfqs/:rfqId/compare
 */

const METRIC_ICON = {
  price: DollarSign,
  lead_time: Clock,
  terms: Gauge,
  risk: Shield,
  performance: TrendingUp,
};


const ProposalComparison = () => {
  const { rfqId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/procurement/rfqs/${rfqId}/comparison`, authHeader);
      setData(r.data);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [rfqId, authHeader, toast]);

  useEffect(() => { load(); }, [load]);

  const proposals = data?.proposals || [];
  const weights = data?.weights || {};
  const best = data?.best || {};
  const winnerId = data?.winner_id;

  const selectWinner = useCallback(async (proposalId) => {
    setBusy(`select-${proposalId}`);
    try {
      await axios.post(`${API_URL}/api/procurement/proposals/${proposalId}/select-winner`, {}, authHeader);
      toast({ title: 'Winner selected' });
      await load();
    } catch (err) {
      toast({ title: 'Selection failed', description: err?.message, variant: 'destructive' });
    } finally { setBusy(null); }
  }, [authHeader, toast, load]);

  const convertToPO = useCallback(async () => {
    setBusy('convert');
    try {
      const r = await axios.post(`${API_URL}/api/procurement/rfqs/${rfqId}/convert-to-po`, {}, authHeader);
      toast({ title: 'Converted to PO', description: `PO number: ${r.data.po_number}` });
      navigate(`/admin/procurement/purchase-orders`);
    } catch (err) {
      toast({ title: 'Conversion failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(null); }
  }, [authHeader, toast, rfqId, navigate]);

  const reScoreAll = useCallback(async () => {
    setBusy('rescore');
    try {
      for (const p of proposals) {
        await axios.post(`${API_URL}/api/procurement/proposals/${p.id}/score`, {}, authHeader);
      }
      toast({ title: 'Proposals re-scored' });
      await load();
    } catch (err) {
      toast({ title: 'Re-score failed', description: err?.message, variant: 'destructive' });
    } finally { setBusy(null); }
  }, [authHeader, proposals, toast, load]);

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="comparison-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading comparison…</div>;
  }

  return (
    <div data-testid="proposal-comparison-page">
      <button onClick={() => navigate('/admin/procurement/rfqs')} className="text-xs text-[#6C757D] hover:text-[#023E8A] flex items-center gap-1 mb-3" data-testid="comparison-back">
        <ArrowLeft className="w-3 h-3" /> Back to RFQs
      </button>

      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Proposal Comparison</h1>
          <p className="text-sm text-[#6C757D]">RFQ <span className="font-mono">{rfqId}</span> · {proposals.length} proposal(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy === 'rescore' || proposals.length === 0} onClick={reScoreAll} data-testid="comparison-rescore-btn">
            {busy === 'rescore' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Re-score'}
          </Button>
          {winnerId && (
            <Button size="sm" onClick={convertToPO} disabled={busy === 'convert'} data-testid="comparison-convert-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy === 'convert' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><ArrowRight className="w-3.5 h-3.5 mr-1" /> Convert winner to PO</>)}
            </Button>
          )}
        </div>
      </div>

      {proposals.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Package className="w-6 h-6 mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-[#6C757D] mb-3">No proposals have been submitted for this RFQ.</p>
            <Link to={`/admin/procurement/rfqs/${rfqId}/proposals/new`} className="text-xs text-[#00B4D8] hover:underline" data-testid="comparison-new-proposal-link">
              <PlusCircle className="w-3.5 h-3.5 inline mr-1" /> Add a proposal
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="comparison-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <Th>Metric</Th>
                    {proposals.map((p) => (
                      <Th key={p.id} className={p.id === winnerId ? 'bg-emerald-50' : ''}>
                        <div className="flex items-center justify-center gap-1">
                          {p.id === winnerId && <Trophy className="w-3.5 h-3.5 text-emerald-600" />}
                          <span>{p.supplier_snapshot?.name || p.supplier_id?.slice(0, 8)}</span>
                        </div>
                        <div className="text-[9px] font-mono text-[#6C757D] font-normal">{p.proposal_number}</div>
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">

                  <Row label="Total Amount" icon={DollarSign}>
                    {proposals.map((p) => (
                      <Td key={p.id} highlight={best.price_proposal_id === p.id} testId={`cell-price-${p.id}`}>
                        <span className="font-mono font-semibold">{p.currency} {p.total_amount?.toLocaleString()}</span>
                      </Td>
                    ))}
                  </Row>

                  <Row label="Lead Time" icon={Clock}>
                    {proposals.map((p) => (
                      <Td key={p.id} highlight={best.lead_proposal_id === p.id} testId={`cell-lead-${p.id}`}>
                        {p.lead_time_days ? `${p.lead_time_days} days` : '—'}
                      </Td>
                    ))}
                  </Row>

                  <Row label="Payment Terms" icon={Gauge}>
                    {proposals.map((p) => (
                      <Td key={p.id} testId={`cell-terms-${p.id}`}>{p.payment_terms || '—'}</Td>
                    ))}
                  </Row>

                  <Row label="Warranty" icon={Shield}>
                    {proposals.map((p) => (
                      <Td key={p.id} testId={`cell-warranty-${p.id}`}>
                        {p.warranty_months ? `${p.warranty_months} months` : '—'}
                      </Td>
                    ))}
                  </Row>

                  {Object.entries(weights).map(([metric, w]) => {
                    const Icon = METRIC_ICON[metric] || Gauge;
                    return (
                      <Row key={metric} label={`${metric.replace('_', ' ')} (weight ${Math.round(w * 100)}%)`} icon={Icon} sub>
                        {proposals.map((p) => (
                          <Td key={p.id} testId={`cell-metric-${metric}-${p.id}`}>
                            <ScoreBar value={p.score_breakdown?.[metric] ?? 0} />
                          </Td>
                        ))}
                      </Row>
                    );
                  })}

                  <tr className="bg-slate-50 font-bold">
                    <Td testId="cell-total-label">
                      <div className="flex items-center gap-1.5">
                        <Trophy className="w-3.5 h-3.5 text-[#FF6B35]" />
                        TOTAL SCORE
                      </div>
                    </Td>
                    {proposals.map((p) => (
                      <Td key={p.id} highlight={best.total_proposal_id === p.id} testId={`cell-total-${p.id}`}>
                        <span className={`text-lg font-bold ${best.total_proposal_id === p.id ? 'text-emerald-600' : 'text-[#2B2D42]'}`}>
                          {p.score != null ? p.score.toFixed(1) : '—'}
                        </span>
                      </Td>
                    ))}
                  </tr>

                  <tr>
                    <Td />
                    {proposals.map((p) => {
                      const isWinner = p.id === winnerId;
                      return (
                        <Td key={p.id} testId={`cell-action-${p.id}`}>
                          {isWinner ? (
                            <span className="text-[10px] font-bold uppercase px-2 py-1 bg-emerald-100 text-emerald-700 rounded inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Winner
                            </span>
                          ) : (
                            <Button size="sm" variant="outline" disabled={busy === `select-${p.id}`}
                                    onClick={() => selectWinner(p.id)}
                                    data-testid={`comparison-select-${p.id}`}>
                              {busy === `select-${p.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Select Winner'}
                            </Button>
                          )}
                        </Td>
                      );
                    })}
                  </tr>

                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-xs text-[#6C757D] font-semibold uppercase tracking-wide text-center ${className}`}>{children}</th>
);

const Td = ({ children, highlight = false, testId }) => (
  <td className={`px-3 py-2 text-center align-middle ${highlight ? 'bg-emerald-50' : ''}`} data-testid={testId}>
    {children}
  </td>
);

const Row = ({ label, icon: Icon, children, sub }) => (
  <tr className="hover:bg-slate-50">
    <td className={`px-3 py-2 text-left ${sub ? 'text-[11px] text-[#6C757D]' : 'text-xs font-semibold text-[#2B2D42]'} capitalize flex items-center gap-1.5`}>
      {Icon && <Icon className="w-3.5 h-3.5 text-[#FF6B35]" />}
      {label}
    </td>
    {children}
  </tr>
);

const ScoreBar = ({ value }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const color = pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`${color} h-full`} style={{ width: `${pct}%` }}></div>
      </div>
      <span className="text-[10px] font-mono tabular-nums">{pct.toFixed(0)}</span>
    </div>
  );
};


export default ProposalComparison;
